import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Rating,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  RateReview as RateReviewIcon,
  Edit as RequestEditIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  Refresh as RefreshIcon,
  Cancel as RejectIcon,
  WarningAmber as WarningIcon,
  Build as EquipmentIcon,
  Schedule as PunctualityIcon,
  Chat as CommunicationIcon,
  AssignmentReturn as ReturnIcon,
  Gavel as RulesIcon,
  HourglassEmpty as PendingIcon,
  Block as BlockIcon,
  PhotoCamera as PhotoCameraIcon,
  ArrowBackIosNew as PrevPhotoIcon,
  ArrowForwardIos as NextPhotoIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'warning', icon: PendingIcon },
  changes_requested: { label: 'Modifs demandées', color: 'info', icon: WarningIcon },
  approved: { label: 'Approuvée', color: 'success', icon: ApproveIcon },
  rejected: { label: 'Rejetée', color: 'error', icon: BlockIcon }
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente' },
  { value: 'changes_requested', label: 'Modifs demandées' },
  { value: 'approved', label: 'Approuvées' },
  { value: 'rejected', label: 'Rejetées' },
  { value: 'reported', label: '🚩 Signalées' },
  { value: 'all', label: 'Toutes' }
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Plus récentes' },
  { value: 'oldest', label: 'Plus anciennes' },
  { value: 'highest_rating', label: 'Meilleures notes' },
  { value: 'lowest_rating', label: 'Moins bonnes notes' }
];

const CATEGORY_OPTIONS = [
  { value: 'routine_review', label: 'Revue de routine' },
  { value: 'content_policy', label: 'Politique de contenu' },
  { value: 'quality_issue', label: 'Problème de qualité' },
  { value: 'user_report', label: 'Signalement utilisateur' },
  { value: 'automated_flag', label: 'Signalement automatisé' },
  { value: 'safety_violation', label: 'Violation sécurité' },
  { value: 'fraud_suspicion', label: 'Suspicion de fraude' }
];

const CATEGORY_VALUES = new Set(CATEGORY_OPTIONS.map((option) => option.value));

const ACTION_LABELS = {
  approve: 'Approuver',
  reject: 'Rejeter',
  request_edit: 'Demander des modifications'
};

const CRITERIA_CONFIG = {
  equipment_respect: { icon: EquipmentIcon, color: '#2196f3' },
  punctuality: { icon: PunctualityIcon, color: '#4caf50' },
  communication: { icon: CommunicationIcon, color: '#ff9800' },
  return_state: { icon: ReturnIcon, color: '#9c27b0' },
  rules_respect: { icon: RulesIcon, color: '#f44336' }
};

const buildErrorMessage = (error) => {
  const apiData = error?.response?.data;
  return (
    apiData?.error ||
    apiData?.message ||
    apiData?.msg ||
    error?.message ||
    'Une erreur est survenue. Veuillez réessayer.'
  );
};

const OwnerRenterReviewModerationPage = () => {
  const { t } = useTranslation(['renterEvaluation']);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: 'pending',
    flaggedOnly: false,
    sort: 'newest',
    resubmittedOnly: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [expandedReview, setExpandedReview] = useState(null);
  const [moderationDialog, setModerationDialog] = useState({
    open: false,
    review: null,
    action: null,
    notes: '',
    rejectionReason: '',
    followUpRequired: false,
    followUpDate: '',
    severity: 'low',
    category: 'routine_review'
  });
  const [reviewPhotos, setReviewPhotos] = useState({});
  const [loadingPhotos, setLoadingPhotos] = useState({});
  const [photoErrors, setPhotoErrors] = useState({});
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState({});

  // États pour la traduction automatique
  const [translating, setTranslating] = useState(false);
  const [translatedNotes, setTranslatedNotes] = useState('');
  const [translationResult, setTranslationResult] = useState(null);
  const [showTranslation, setShowTranslation] = useState(false);

  const limitOptions = useMemo(() => [10, 20, 50], []);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: rowsPerPage,
        status: filters.status,
        flagged_only: filters.flaggedOnly,
        sort: filters.sort,
        resubmitted_only: filters.resubmittedOnly,
        _t: Date.now()
      };

      const response = await api.get('/reviews/admin/owner-renter/pending', { params });
      const payload = response?.data?.data || {};
      const queueReviews = Array.isArray(payload?.data) ? payload.data : [];
      const queuePagination = payload?.pagination || {
        total: 0,
        totalPages: 1,
        page: params.page,
        limit: params.limit
      };

      setReviews(queueReviews);
      setPagination(queuePagination);
    } catch (err) {
      console.error('Error loading owner-renter reviews:', err);
      const message = buildErrorMessage(err);
      setError(message);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [filters.flaggedOnly, filters.resubmittedOnly, filters.sort, filters.status, page, rowsPerPage]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleModerationAction = (review, action) => {
    setModerationDialog({
      open: true,
      review,
      action,
      notes: '',
      rejectionReason: '',
      category: 'routine_review'
    });
  };

  const closeModerationDialog = () => {
    setModerationDialog({
      open: false,
      review: null,
      action: null,
      notes: '',
      rejectionReason: '',
      followUpRequired: false,
      followUpDate: '',
      severity: 'low',
      category: 'routine_review'
    });
    // Réinitialiser les états de traduction
    setTranslatedNotes('');
    setTranslationResult(null);
    setShowTranslation(false);
  };

  const handleModerationChange = (key, value) => {
    setModerationDialog((prev) => ({
      ...prev,
      [key]: value
    }));
    // Réinitialiser la traduction si les notes changent
    if (key === 'notes') {
      setTranslatedNotes('');
      setTranslationResult(null);
      setShowTranslation(false);
    }
  };

  const handleTranslateNotes = async () => {
    const { review, notes } = moderationDialog;
    
    if (!notes || notes.trim().length < 10) {
      setFeedback({
        type: 'warning',
        message: 'Les notes doivent contenir au moins 10 caractères pour être traduites.'
      });
      return;
    }

    setTranslating(true);
    try {
      const response = await api.post(`/reviews/admin/owner-renter/${review.id}/translate-moderation`, {
        notes: notes.trim()
      });

      if (response.data.success) {
        const translation = response.data.translation;
        setTranslationResult(translation);

        if (translation.needsTranslation) {
          if (translation.translatedNotes) {
            setTranslatedNotes(translation.translatedNotes);
            setShowTranslation(true);
            setFeedback({
              type: 'success',
              message: `Traduction générée en ${translation.targetLanguage}`
            });
          } else {
            setFeedback({
              type: 'error',
              message: response.data.message || 'Erreur lors de la traduction'
            });
          }
        } else {
          setFeedback({
            type: 'info',
            message: response.data.message
          });
        }
      } else {
        setFeedback({
          type: 'error',
          message: response.data.message || 'Erreur lors de la traduction'
        });
      }
    } catch (error) {
      console.error('Error translating moderation notes:', error);
      if (error.response?.status === 503) {
        setFeedback({
          type: 'error',
          message: 'Service de traduction indisponible'
        });
      } else if (error.response?.status === 456) {
        setFeedback({
          type: 'warning',
          message: 'Quota de traduction épuisé. Veuillez traduire manuellement.'
        });
      } else {
        setFeedback({
          type: 'error',
          message: 'Erreur lors de la traduction'
        });
      }
    } finally {
      setTranslating(false);
      setTimeout(() => setFeedback(null), 5000);
    }
  };

  const handleModerationSubmit = async () => {
    try {
      const { review, action, notes, rejectionReason, followUpRequired, followUpDate, severity, category } = moderationDialog;
      
      // Validation: notes obligatoires pour reject et request_edit
      const trimmedNotes = notes ? notes.trim() : '';
      if ((action === 'reject' || action === 'request_edit') && !trimmedNotes) {
        setFeedback({
          type: 'error',
          message: 'Les notes de modération sont obligatoires pour cette action.'
        });
        return;
      }
      
      const normalizedCategory = CATEGORY_VALUES.has(category)
        ? category
        : 'routine_review';

      const payload = {
        action,
        notes: trimmedNotes || undefined,
        category: normalizedCategory,
        severity,
        follow_up_required: followUpRequired,
        follow_up_date: followUpRequired && followUpDate ? followUpDate : undefined
      };

      if (action === 'reject') {
        payload.rejection_reason = rejectionReason;
      }

      await api.patch(`/reviews/admin/owner-renter/${review.id}/moderate`, payload);

      const reviewId = review.id;
      const actionDone = action;

      setFeedback({
        type: 'success',
        message: `Avis ${ACTION_LABELS[action].toLowerCase()} avec succès.`
      });

      closeModerationDialog();
      // Mise à jour optimiste immédiate → préserve le focus / scroll
      const statusMap = { approve: 'approved', reject: 'rejected', request_edit: 'changes_requested' };
      const newStatus = statusMap[actionDone];
      if (newStatus) {
        setReviews(prev => prev.map(r =>
          r.id === reviewId ? { ...r, moderation_status: newStatus, moderator_notes: payload.notes } : r
        ));
      }
      // Rechargement en arrière-plan
      loadReviews();

      setTimeout(() => setFeedback(null), 5000);
    } catch (err) {
      const message = buildErrorMessage(err);
      setFeedback({
        type: 'error',
        message: `Erreur lors de la modération: ${message}`
      });
    }
  };

  const toggleReviewDetails = async (reviewId) => {
    const isCurrentlyExpanded = expandedReview === reviewId;
    setExpandedReview(isCurrentlyExpanded ? null : reviewId);
    
    // Charger les photos quand on expand la review
    if (!isCurrentlyExpanded && !reviewPhotos[reviewId] && !loadingPhotos[reviewId]) {
      setLoadingPhotos((prev) => ({ ...prev, [reviewId]: true }));
      setPhotoErrors((prev) => ({ ...prev, [reviewId]: null }));
      
      try {
        const response = await api.get(`/reviews/owner-renter/${reviewId}/photos`, {
          params: { _t: Date.now() }
        });
        const photos = response?.data?.data?.data?.photos || [];
        setReviewPhotos((prev) => ({ ...prev, [reviewId]: photos }));
        setCurrentPhotoIndex((prev) => ({ ...prev, [reviewId]: 0 }));
      } catch (err) {
        console.error('Error loading photos:', err);
        setPhotoErrors((prev) => ({ ...prev, [reviewId]: 'Erreur lors du chargement des photos' }));
      } finally {
        setLoadingPhotos((prev) => ({ ...prev, [reviewId]: false }));
      }
    }
  };

  const isActionDisabled = (review, action) => {
    const status = review?.moderation_status;
    if (!status) return false;
    if (action === 'approve') return status === 'approved';
    if (action === 'reject') return status === 'rejected';
    if (action === 'request_edit') return status === 'changes_requested';
    return false;
  };

  const renderReviewDetails = (review) => {
    if (!review) {
      return null;
    }

    const statusKey = review.moderation_status || 'pending';
    const statusConfig = STATUS_CONFIG[statusKey] || STATUS_CONFIG.pending;
    const StatusIcon = statusConfig.icon || WarningIcon;
    const recommendationValue = review.criteria_scores?.would_recommend ?? review.would_recommend ?? undefined;
    const criteriaEntries = review.criteria_scores
      ? Object.entries(review.criteria_scores).filter(([key]) => key !== 'would_recommend')
      : [];
    const formattedCreatedAt = review.created_at
      ? format(new Date(review.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })
      : null;

    return (
      <Stack spacing={3}>
        <Alert severity={statusConfig.color} icon={<StatusIcon fontSize="small" />}>
          <Typography variant="body2" fontWeight={600}>
            {statusConfig.label}
          </Typography>
          {statusKey === 'changes_requested' && review.moderator_notes && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {review.moderator_notes}
            </Typography>
          )}
          {statusKey === 'rejected' && review.moderator_notes && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Raison : {review.moderator_notes}
            </Typography>
          )}
        </Alert>

        {recommendationValue !== undefined && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Recommandation
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {recommendationValue ? (
                <ApproveIcon sx={{ color: 'success.main' }} />
              ) : (
                <RejectIcon sx={{ color: 'error.main' }} />
              )}
              <Typography
                variant="body1"
                color={recommendationValue ? 'success.main' : 'error.main'}
                fontWeight={600}
              >
                {recommendationValue
                  ? 'Locataire recommandé'
                  : 'Locataire non recommandé'}
              </Typography>
            </Box>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Note globale
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Rating value={review.rating || 0} readOnly size="large" />
            <Typography variant="h6" color="primary">
              {(review.rating ?? 0)}/5
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Commentaire général
          </Typography>
          {review.comment ? (
            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                "{review.comment}"
              </Typography>
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Aucun commentaire fourni
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Critères détaillés
          </Typography>
          {criteriaEntries.length > 0 ? (
            <Stack spacing={2}>
              {criteriaEntries.map(([key, scoreData]) => {
                const config = CRITERIA_CONFIG[key];
                if (!config) return null;

                const IconComponent = config.icon;
                const rating = typeof scoreData === 'number' ? scoreData : scoreData?.rating;
                const comment = typeof scoreData === 'object' ? scoreData?.comment : null;

                return (
                  <Paper
                    key={key}
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? `${config.color}15`
                          : `${config.color}25`,
                      border: '1px solid',
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? `${config.color}40`
                          : `${config.color}60`,
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <IconComponent
                        sx={{
                          color: config.color,
                          fontSize: 28,
                          mt: 0.5
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1" fontWeight={600}>
                            {t(`criteria.${key}.title`)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Rating value={rating || 0} readOnly size="small" />
                            <Typography variant="body2" fontWeight={600}>
                              {(rating ?? 0)}/5
                            </Typography>
                          </Box>
                        </Box>
                        {comment && (
                          <Typography variant="body2" color="text.secondary">
                            {comment}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Aucun critère détaillé fourni
            </Typography>
          )}
        </Box>

        {/* Section Photos */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoCameraIcon fontSize="small" />
            Photos jointes par le propriétaire
          </Typography>
          {loadingPhotos[review.id] ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Chargement des photos...
              </Typography>
            </Box>
          ) : photoErrors[review.id] ? (
            <Alert severity="warning">{photoErrors[review.id]}</Alert>
          ) : reviewPhotos[review.id] && reviewPhotos[review.id].length > 0 ? (
            <Box>
              <Box sx={{ position: 'relative', width: '100%', height: 300, backgroundColor: 'grey.100', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                {/* Image principale */}
                <Box
                  component="img"
                  src={reviewPhotos[review.id][currentPhotoIndex[review.id] || 0]}
                  alt={`Photo ${(currentPhotoIndex[review.id] || 0) + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />

                {/* Navigation si plusieurs photos */}
                {reviewPhotos[review.id].length > 1 && (
                  <>
                    <IconButton
                      onClick={() => setCurrentPhotoIndex((prev) => ({
                        ...prev,
                        [review.id]: ((prev[review.id] || 0) - 1 + reviewPhotos[review.id].length) % reviewPhotos[review.id].length
                      }))}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                      }}
                    >
                      <PrevPhotoIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => setCurrentPhotoIndex((prev) => ({
                        ...prev,
                        [review.id]: ((prev[review.id] || 0) + 1) % reviewPhotos[review.id].length
                      }))}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                      }}
                    >
                      <NextPhotoIcon fontSize="small" />
                    </IconButton>

                    {/* Indicateur de position */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: '0.75rem'
                      }}
                    >
                      {(currentPhotoIndex[review.id] || 0) + 1} / {reviewPhotos[review.id].length}
                    </Box>
                  </>
                )}
              </Box>

              {/* Miniatures */}
              {reviewPhotos[review.id].length > 1 && (
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                  {reviewPhotos[review.id].map((photo, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={photo}
                      alt={`Miniature ${index + 1}`}
                      onClick={() => setCurrentPhotoIndex((prev) => ({ ...prev, [review.id]: index }))}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: (currentPhotoIndex[review.id] || 0) === index ? '2px solid' : '1px solid',
                        borderColor: (currentPhotoIndex[review.id] || 0) === index ? 'primary.main' : 'divider',
                        opacity: (currentPhotoIndex[review.id] || 0) === index ? 1 : 0.6,
                        transition: 'all 0.2s',
                        '&:hover': { opacity: 1 },
                        flexShrink: 0
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Aucune photo jointe
            </Typography>
          )}
        </Box>

        {formattedCreatedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
            Évalué le {formattedCreatedAt}
          </Typography>
        )}
      </Stack>
    );
  };

  const renderReviewCard = (review) => {
    const isExpanded = expandedReview === review.id;
    const statusInfo = STATUS_CONFIG[review.moderation_status] || STATUS_CONFIG.pending;

    return (
      <Paper key={review.id} elevation={2} sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <RateReviewIcon color="primary" />
              <Typography variant="h6">
                Avis pour {review.reviewee?.name || 'Locataire inconnu'}
              </Typography>
              <Chip
                label={statusInfo.label}
                color={statusInfo.color}
                size="small"
              />
              {review.flagged && (
                <Chip
                  icon={<FlagIcon />}
                  label="Signalé"
                  color="error"
                  size="small"
                />
              )}
              {review.resubmission_count > 0 && (
                <Chip
                  label={`Resoumis ${review.resubmission_count}x`}
                  color="info"
                  size="small"
                />
              )}
            </Stack>
            <IconButton onClick={() => toggleReviewDetails(review.id)} size="small">
              <ExpandMoreIcon
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              />
            </IconButton>
          </Stack>

          {/* Alert de signalement avec détails */}
          {review.reportInfo && review.reportInfo.report_count > 0 && (
            <Alert severity="error" icon={<WarningIcon />}>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                ⚠️ Cet avis a été signalé {review.reportInfo.report_count} fois
              </Typography>
              <Typography variant="body2" paragraph>
                Dernier signalement: {review.reportInfo.last_reported_at && format(new Date(review.reportInfo.last_reported_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </Typography>
              
              {/* Liste de tous les signalements */}
              {review.reportInfo.details && review.reportInfo.details.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" fontWeight="600" display="block" gutterBottom>
                    Tous les signalements ({review.reportInfo.details.length}):
                  </Typography>
                  <Stack spacing={1} mt={1}>
                    {review.reportInfo.details.map((detail, index) => (
                      <Box 
                        key={detail.id || index} 
                        sx={{ 
                          bgcolor: 'error.50', 
                          p: 1.5, 
                          borderRadius: 1,
                          borderLeft: '3px solid',
                          borderColor: 'error.main'
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="start" spacing={1}>
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight="500">
                              {detail.reason}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                              Par: {detail.reporter?.name || detail.reporter?.email || 'Utilisateur'} • 
                              Le {format(new Date(detail.reported_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                            </Typography>
                          </Box>
                          <Chip 
                            label={`#${index + 1}`} 
                            size="small" 
                            color="error" 
                            variant="outlined"
                          />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Alert>
          )}

          {/* Summary */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Réservation
              </Typography>
              <Typography variant="body1">
                #{review.reservation?.id || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Propriétaire (auteur)
              </Typography>
              <Typography variant="body1">
                {review.reviewer?.name || 'Inconnu'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Note donnée
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Rating value={review.rating || 0} readOnly size="small" />
                <Typography variant="body2">({review.rating || 0}/5)</Typography>
              </Stack>
            </Grid>
            {(review.criteria_scores?.would_recommend != null || review.would_recommend != null) && (
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Recommandation
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {(review.criteria_scores?.would_recommend ?? review.would_recommend) ? (
                    <ApproveIcon sx={{ color: 'success.main', fontSize: 18 }} />
                  ) : (
                    <RejectIcon sx={{ color: 'error.main', fontSize: 18 }} />
                  )}
                  <Typography variant="body2" color={(review.criteria_scores?.would_recommend ?? review.would_recommend) ? 'success.main' : 'error.main'} fontWeight={600}>
                    {(review.criteria_scores?.would_recommend ?? review.would_recommend) ? 'Locataire recommandé' : 'Non recommandé'}
                  </Typography>
                </Stack>
              </Grid>
            )}
          </Grid>

          {/* Actions de modération - TOUJOURS VISIBLES, bouton actif désactivé */}
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => handleModerationAction(review, 'approve')}
                disabled={isActionDisabled(review, 'approve')}
              >
                Approuver
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<RequestEditIcon />}
                onClick={() => handleModerationAction(review, 'request_edit')}
                disabled={isActionDisabled(review, 'request_edit')}
              >
                Demander modifs
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => handleModerationAction(review, 'reject')}
                disabled={isActionDisabled(review, 'reject')}
              >
                Rejeter
              </Button>
            </Stack>
          </>

          {/* Détails étendus */}
          <Collapse in={isExpanded}>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Détails complets de la review
                </Typography>
                {renderReviewDetails(review)}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de création
                  </Typography>
                  <Typography variant="body1">
                    {review.created_at
                      ? format(new Date(review.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })
                      : 'N/A'}
                  </Typography>
                </Grid>
                {review.resubmitted_at && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Dernière resoumission
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(review.resubmitted_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {review.moderator_notes && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Notes de modération précédentes
                  </Typography>
                  <Alert severity="info">{review.moderator_notes}</Alert>
                </Box>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Modération Avis Propriétaire → Locataire</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={async () => {
              await loadReviews();
              // Recharger les photos du panneau actuellement ouvert
              if (expandedReview) {
                setReviewPhotos(prev => { const next = { ...prev }; delete next[expandedReview]; return next; });
                const res = await api.get(`/reviews/owner-renter/${expandedReview}/photos`, { params: { _t: Date.now() } });
                const photos = res?.data?.data?.data?.photos || [];
                setReviewPhotos(prev => ({ ...prev, [expandedReview]: photos }));
              }
            }}
            disabled={loading}
          >
            Actualiser
          </Button>
        </Stack>

        {/* Feedback */}
        {feedback && (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        {/* Filtres */}
        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.status}
                label="Statut"
                onChange={(event) => {
                  setFilters((prev) => ({ ...prev, status: event.target.value }));
                  setPage(1);
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Trier par</InputLabel>
              <Select
                value={filters.sort}
                label="Trier par"
                onChange={(event) => {
                  setFilters((prev) => ({ ...prev, sort: event.target.value }));
                  setPage(1);
                }}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 160 }} size="small">
              <InputLabel>Résultats / page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Résultats / page"
                onChange={(event) => {
                  const nextValue = Number(event.target.value) || 10;
                  setRowsPerPage(nextValue);
                  setPage(1);
                }}
              >
                {limitOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option} par page
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={filters.flaggedOnly}
                  onChange={(event) => {
                    setFilters((prev) => ({ ...prev, flaggedOnly: event.target.checked }));
                    setPage(1);
                  }}
                />
              }
              label="Signalés uniquement"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={filters.resubmittedOnly}
                  onChange={(event) => {
                    setFilters((prev) => ({ ...prev, resubmittedOnly: event.target.checked }));
                    setPage(1);
                  }}
                />
              }
              label="Resoumis uniquement"
            />
          </Stack>
        </Paper>

        {/* Error */}
        {error && <Alert severity="error">{error}</Alert>}

        {/* Liste des avis */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : !Array.isArray(reviews) || reviews.length === 0 ? (
          <Alert severity="info">Aucun avis propriétaire → locataire trouvé pour ces critères.</Alert>
        ) : (
          <Stack spacing={2}>
            {reviews.map((review) => renderReviewCard(review))}
          </Stack>
        )}

        {/* Pagination */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {pagination.total || 0} avis au total
          </Typography>
          <Pagination
            count={pagination.totalPages || 1}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Stack>

        {/* Dialog de modération */}
        <Dialog open={moderationDialog.open} onClose={closeModerationDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {moderationDialog.action ? ACTION_LABELS[moderationDialog.action] : 'Action de modération'}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              {moderationDialog.review && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Avis de {moderationDialog.review.reviewer?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pour: {moderationDialog.review.reviewee?.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Rating value={moderationDialog.review.rating || 0} readOnly size="small" />
                    <Typography variant="body2">({moderationDialog.review.rating}/5)</Typography>
                  </Stack>
                </Box>
              )}

              <Divider />

              {moderationDialog.action === 'reject' && (
                <TextField
                  label="Raison du rejet *"
                  multiline
                  rows={3}
                  value={moderationDialog.rejectionReason}
                  onChange={(e) =>
                    setModerationDialog((prev) => ({ ...prev, rejectionReason: e.target.value }))
                  }
                  placeholder="Expliquez pourquoi cet avis est rejeté..."
                  required
                  helperText="Cette raison sera communiquée au propriétaire"
                />
              )}

              <TextField
                label="Notes de modération"
                multiline
                rows={4}
                value={moderationDialog.notes}
                onChange={(e) => handleModerationChange('notes', e.target.value)}
                placeholder="Ajoutez des notes internes sur cette modération..."
                required={moderationDialog.action === 'reject' || moderationDialog.action === 'request_edit'}
                error={(moderationDialog.action === 'reject' || moderationDialog.action === 'request_edit') && !moderationDialog.notes.trim()}
                helperText={
                  (moderationDialog.action === 'reject' || moderationDialog.action === 'request_edit')
                    ? "Les notes sont obligatoires pour le rejet ou les demandes de modification (utilisées comme raison du signalement)"
                    : "Ces notes sont visibles uniquement par les administrateurs et peuvent être traduites"
                }
                disabled={translating}
              />

              {/* Bouton de traduction */}
              <Box display="flex" alignItems="center" gap={2} mt={2} mb={2}>
                <Button
                  variant="outlined"
                  startIcon={translating ? <CircularProgress size={16} /> : <TranslateIcon />}
                  onClick={handleTranslateNotes}
                  disabled={!moderationDialog.notes.trim() || moderationDialog.notes.trim().length < 10 || translating}
                  size="small"
                >
                  {translating ? 'Traduction...' : 'Traduire automatiquement'}
                </Button>
                {translationResult && (
                  <Chip 
                    label={`Vers: ${translationResult.targetLanguage}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Affichage de la traduction proposée */}
              {showTranslation && translatedNotes && (
                <Box mb={2} p={2} sx={{ backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Typography variant="subtitle2" color="success.main" gutterBottom>
                    Traduction proposée en {translationResult?.targetLanguage} :
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                    "{translatedNotes}"
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Alert severity="info" sx={{ fontSize: '0.875rem', flex: 1, mr: 2 }}>
                      Vérifiez la traduction et cliquez sur "Utiliser cette traduction" pour l'insérer dans le champ notes.
                    </Alert>
                    <Button
                      variant="contained"
                      size="small"
                      color="success"
                      onClick={() => {
                        setModerationDialog((prev) => ({ ...prev, notes: translatedNotes }));
                        setFeedback({
                          type: 'success',
                          message: 'Traduction utilisée dans le champ notes'
                        });
                        setTimeout(() => setFeedback(null), 3000);
                      }}
                    >
                      Utiliser cette traduction
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Notes de traduction en cas d'erreur */}
              {translationResult && translationResult.error && (
                <Box mb={2}>
                  <Alert severity="warning">
                    {translationResult.error === 'Quota épuisé' 
                      ? 'Quota de traduction épuisé. Veuillez traduire manuellement les notes dans la langue du propriétaire.' 
                      : 'Erreur de traduction. Veuillez vérifier que vos notes sont en français et réessayer.'}
                  </Alert>
                </Box>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <FormControl sx={{ minWidth: 160 }} size="small">
                  <InputLabel>Sévérité</InputLabel>
                  <Select
                    value={moderationDialog.severity}
                    label="Sévérité"
                    onChange={(event) => handleModerationChange('severity', event.target.value)}
                  >
                    <MenuItem value="low">Faible</MenuItem>
                    <MenuItem value="medium">Moyenne</MenuItem>
                    <MenuItem value="high">Élevée</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={CATEGORY_VALUES.has(moderationDialog.category) ? moderationDialog.category : 'routine_review'}
                    label="Catégorie"
                    onChange={(event) => handleModerationChange('category', event.target.value)}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Choisissez la raison principale pour l'action de modération.</FormHelperText>
                </FormControl>
              </Stack>

              <FormControlLabel
                control={(
                  <Switch
                    checked={moderationDialog.followUpRequired}
                    onChange={(event) => handleModerationChange('followUpRequired', event.target.checked)}
                  />
                )}
                label="Planifier un suivi"
              />

              {moderationDialog.followUpRequired && (
                <TextField
                  label="Date de suivi"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={moderationDialog.followUpDate}
                  onChange={(event) => handleModerationChange('followUpDate', event.target.value)}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModerationDialog}>Annuler</Button>
            <Button
              variant="contained"
              onClick={handleModerationSubmit}
              disabled={
                (moderationDialog.action === 'reject' && !moderationDialog.rejectionReason.trim()) ||
                ((moderationDialog.action === 'reject' || moderationDialog.action === 'request_edit') && !moderationDialog.notes.trim())
              }
            >
              Confirmer
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default OwnerRenterReviewModerationPage;
